import React, { useEffect, useContext, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
// import dayjs from "dayjs";
import { Icons } from "../../config/MuiIcons.config";
import wordCompletionGameService from "../../services/WordCompletionGameService";
import { AppGlobalContext } from "../../contexts/AppContext.context";
import { useSnackbar } from "../../contexts/Snackbar.context";
import { useBackdrop } from "../../contexts/BackDrop.context";
import { SNACKBAR_VARIANT, APP_MESSAGE } from "../../config/Messages.config";
import SpeedRunCard from "./components/SpeedRunCard/SpeedRunCard";
import LeaderboardCard from "./components/LeaderboardCard/LeaderboardCard";
import MUILoader from "../../components/MUILoader/MUILoader";

const DailyChallenges = () => {
  const { authUserDetails } = useContext(AppGlobalContext);
  const { showSnackbar } = useSnackbar();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const navigate = useNavigate();
  const [activePassage, setActivePassage] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [isChallengeCompleted, setIsChallengeCompleted] = useState(false);
  const [loadingToCheckChallenge, setLoadingToCheckChallenge] = useState(true);
  // loading state
  const [pageLoading, setPageLoading] = useState(true);
  const [loadingState, setLoadingState] = useState({
    activePassage: true,
    leaderboard: true,
  });

  // Function to update loading state
  const setLoading = (key, value) => {
    setLoadingState((prev) => {
      return { ...prev, [key]: value };
    });
  };

  // App Messages
  const { WORD_COMPLETION_GAME } = APP_MESSAGE;
  const { FETCH } = WORD_COMPLETION_GAME;
  const { PASSAGE, GAME_SESSION, LEADERBOARD, GAME_SESSION_COMPLETE } = FETCH;

  const getActivePassage = async () => {
    try {
      setLoading("activePassage", true);
      const response = await wordCompletionGameService.getActivePassage();
      const { data, statusCode } = response;

      if (!data || statusCode !== 200) {
        // showSnackbar(PASSAGE.FAILED, SNACKBAR_VARIANT.ERROR);
        setActivePassage(null);
        return null;
      }
      setActivePassage(data);
    } catch (error) {
      console.log(error);
      showSnackbar(PASSAGE.FAILED, SNACKBAR_VARIANT.ERROR);
    } finally {
      setLoading("activePassage", false);
    }
  };

  const getLeaderboard = async () => {
    try {
      setLoading("leaderboard", true);
      const response = await wordCompletionGameService.getLeaderboard({
        limit: 20,
      });
      const { data, statusCode } = response;

      if (!data || statusCode !== 200) {
        showSnackbar(LEADERBOARD.FAILED, SNACKBAR_VARIANT.ERROR);
        return null;
      }
      setLeaderboard(data);
    } catch (error) {
      console.log(error);
      showSnackbar(LEADERBOARD.FAILED, SNACKBAR_VARIANT.ERROR);
    } finally {
      setLoading("leaderboard", false);
    }
  };

  const checkIfChallengeCompleted = async () => {
    if (!activePassage) return;
    setLoadingToCheckChallenge(true);
    // First, check if user has an existing session
    try {
      const existingSessionResponse =
        await wordCompletionGameService.getUserGameSession({
          passageId: activePassage.id,
        });

      if (
        existingSessionResponse?.data &&
        existingSessionResponse?.statusCode === 200
      ) {
        const existingSession = existingSessionResponse.data;

        // Check if session exists and passageId matches
        if (existingSession.passageId === activePassage.id) {
          // If session is completed and lastUpdatedAt is from today, restrict access
          if (existingSession.isCompleted === true) {
            // User has already completed today's challenge - restrict access
            setIsChallengeCompleted(true);
            return;
          } else {
            setIsChallengeCompleted(false);
            return;
          }
        }
      }
    } catch (error) {
      // If getUserGameSession fails (e.g., no session exists), continue with normal flow
      console.log(error);
    } finally {
      setLoadingToCheckChallenge(false);
    }
  }

  useEffect(() => {
    if (!authUserDetails?.uid) return;
    getActivePassage();
    getLeaderboard();
  }, [authUserDetails]);

  useEffect(() => {
    if (activePassage) {
      checkIfChallengeCompleted();
    }
  }, [activePassage]);

  // Handle global loading state
  useEffect(() => {
    // Global loading state
    const isAllLoadingComplete = Object.values(loadingState).every(
      (status) => !status
    );

    if (!isAllLoadingComplete) {
      setPageLoading(true);
    } else {
      setPageLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingState]);

  // Check if date is today
  // const isToday = (dateString) => {
  //   if (!dateString) return false;
  //   const date = dayjs(dateString);
  //   const today = dayjs();
  //   return date.isSame(today, "day");
  // };

  const handlePlayClick = async () => {
    if (!activePassage) {
      showSnackbar("No active passage available", SNACKBAR_VARIANT.ERROR);
      return;
    }

    try {
      showBackdrop();
      // Start or resume game session
      const response = await wordCompletionGameService.startGameSession({
        passageId: activePassage.id,
      });

      const { statusCode, data } = response;

      if (data && (statusCode === 200 || statusCode === 201)) {
        const sessionData = data;
        const sessionId = sessionData.id;

        // Save passage and session to localStorage
        const gameData = {
          passage: activePassage,
          session: sessionData,
          completedWords: sessionData.completedWords || [],
          elapsedTime: sessionData.elapsedTime || 0,
          isCompleted: sessionData.isCompleted || false,
        };

        localStorage.setItem(
          "wordCompletionGameData",
          JSON.stringify({
            ...gameData,
            sessionId,
            lastUpdated: Date.now(),
          })
        );

        // Navigate to game page
        hideBackdrop();
        navigate(`/daily-challenge/word-completion?sessionId=${sessionId}`);
      } else {
        showSnackbar(
          GAME_SESSION?.FAILED || "Failed to start game session",
          SNACKBAR_VARIANT.ERROR
        );
        hideBackdrop();
      }
    } catch (error) {
      console.error("Error starting game session:", error);
      showSnackbar(
        GAME_SESSION?.FAILED || "Failed to start game session",
        SNACKBAR_VARIANT.ERROR
      );
      hideBackdrop();
    }
  };

  return (
    <React.Fragment>
      {pageLoading ? (
        <MUILoader />
      ) : (
        <Container
          className="p-0 m-0 w-100"
          style={{
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top row: back + centered title */}
          <div
            className="mb-2"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <div
              onClick={() => navigate("/")}
              style={{
                cursor: "pointer",
              }}
            >
              <Icons.ChevronLeftIcon
                fontSize="large"
                sx={{ color: "#2D3748" }}
              />
            </div>

            <Typography
              className="plus-jakarta-sans-semi-bold-600 ms-2"
              sx={{ fontSize: "19px" }}
            >
              Daily Challenge
            </Typography>
          </div>

          {activePassage !== null ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: "center",
                width: "100%",
              }}
            >
              <SpeedRunCard
                activePassage={activePassage}
                onPlayClick={handlePlayClick}
                loading={loadingState.activePassage}
                isChallengeCompleted={{
                  isCompleted: isChallengeCompleted,
                  loadingForChallenge: loadingToCheckChallenge,
                }}
              />

              <LeaderboardCard
                leaderboard={leaderboard}
                loading={loadingState.leaderboard}
              />
            </Box>
          ) : (
            <div
              className="d-flex align-items-center justify-content-center mt-3"
              style={{ height: "400px" }}
            >
              <Typography variant="h6" className="text-center">
                No active passage available
              </Typography>
            </div>
          )}
        </Container>
      )}
    </React.Fragment>
  );
};

export default DailyChallenges;
